<script lang="ts" setup>
import moment from 'moment';
import { ref, onMounted } from 'vue';
import './PolizasMaritimas.scss';
import PolicyRepository from '../../repositories/policies.repository';
import { IPolicie } from '../../types/policies.type';
import storeAuth from '@store/auth';
import { isAuditUser } from '../../helpers/auditUser';
import { formatterPrice, formatDateAndHourSpanish } from '../../helpers';
import { BModal } from 'bootstrap-vue-next';
import { useI18n } from 'vue-i18n'
import ApiPolicyFilters from '../../components/Polizas/ApiPolicyFilters.vue';
import {
  ApiPolicyCompanyOption,
  ApiPolicyFilters as ApiPolicyFilterValues,
} from '../../types/policies.type';
import DataTable from '@components/DataTable.vue';
import { useToast } from '../../composables/useToast';

const isLoadingPolicies = ref<boolean>(false);
const isDownloadingExcel = ref<boolean>(false);

const { t } = useI18n();
const { $toast } = useToast();
const { user } = storeAuth();
const isAudit = isAuditUser(user?.email);
const policyRepository = new PolicyRepository();
const policiesData = ref<IPolicie[]>([]);
const paginationData = ref();
const isOpenDialog = ref<boolean>(false);
const idDeleteItem = ref<number | null>(null);
const currentPage = ref<number>(1);
const isModalOpen = ref<boolean>(false);
const selectedPolicieInfo = ref<any>(null);
const isModalContainerOpen = ref<boolean>(false);
const detailContainer = ref<any>(null);
const appliedFilters = ref<ApiPolicyFilterValues>({});
const companies: ApiPolicyCompanyOption[] = [
  { label: 'Vink', value: 1 },
  { label: 'ControlMatch', value: 2 },
];

const loadPolicies = async (page: number = 1) => {
  isLoadingPolicies.value = true;
  try {
    const { policies } = await policyRepository.getPolicies({
      ...appliedFilters.value,
      page,
      typePolicyTransport: 'Maritimo',
    });
    policiesData.value = policies.policies ?? [];
    paginationData.value = policies.pagination;
    currentPage.value = page;
  } finally {
    isLoadingPolicies.value = false;
  }
};

onMounted(() => {
  loadPolicies(1);
});

const fieldsTable: any = ref([
  {
    key: 'id',
    label: t('polizas.polizas.id'),
    sortable: true,
  },
  {
    key: 'policyNumber',
    label: t('polizas.polizas.policyNumber'),
  },
  {
    key: 'insuredName',
    label: t('global.asegurado'),
    sortable: true,
  },
  {
    key: 'asset',
    label: t('polizas.polizas.id_de_carga'),
    formatter: (e: any) => e?.id || null,
  },
  {
	key: 'asset',
	label: t('polizas.polizas.descripcion_de_la_carga'),
	formatter: (e: any) => e?.description || null,
  },
  {
    key: 'policyType',
    label: t('polizas.polizas.tipo_de_carga'),
  },
  {
    key: 'customCatalogIds',
    label: t('polizas.polizas.ids_del_catalogo'),
  },
  {
    key: 'customCatalog',
    label: t('polizas.polizas.descripcion_del_catalogo'),
  },
  {
    key: 'createdAt',
    label: t('polizas.polizas.fecha_de_creacion'),
    formatter: (e: any) => moment(e).format('DD-MM-YYYY HH:mm'),
  },
  {
    key: 'status',
    label: t('global.estado'),
    formatter: (e: any) => (e == null || e === 'Pendiente' ? 'En progreso' : 'Cancelada'),
  },
  {
    key: 'indemnificationLimit',
    label: t('polizas.polizas.suma_asegurada'),
    formatter: (e: any) => (e ? formatterPrice(e) : null),
  },
  {
    key: 'details',
    label: t('global.detalles'),
  },
  {
    key: 'actions',
    label: t('global.acciones'),
  },
]);

const handleDelete = ({ id }: any) => {
  isOpenDialog.value = true;
  idDeleteItem.value = id;
};
const handleConfirmDelete = async () => {
  if (idDeleteItem.value !== null) {
    try {
      await policyRepository.deletePolicy(idDeleteItem.value, {typePolicyTransport: 'Maritimo'});
    } catch (error: any) {
      $toast?.error(error?.message ?? t('global.ocurrio_un_error'));
      isOpenDialog.value = false;
      return;
    }
  }
  await loadPolicies(currentPage.value);
  isOpenDialog.value = false;
};

const onChangePage = async (page: number) => {
  await loadPolicies(page);
};

const downloadExcel = async (filters: ApiPolicyFilterValues) => {
  isDownloadingExcel.value = true;
  try {
    await policyRepository.getExcelPolicies({
      search: filters.search,
      clientId: filters.clientId,
      certificateNumber: filters.certificateNumber,
      startDate: filters.startDate || null,
      endDate: filters.endDate || null,
      company: filters.company ?? null,
      toExcel: true,
      typePolicyTransport: 'Maritimo',
    });
  } finally {
    isDownloadingExcel.value = false;
  }
};

const handleOnSubmit = async (filters: ApiPolicyFilterValues) => {
  appliedFilters.value = filters;
  await loadPolicies(1);
};

const handleClearFilters = async () => {
  appliedFilters.value = {};
  await loadPolicies(1);
};

const getRowTable = (policiesData: any, type: string) => {
  const catalogue = policiesData?.data?.item?.catalogue;

  if (!catalogue) {
    return null;
  }

  const concatenatedString = catalogue
    .map((item: any) => {
      switch (type) {
        case 'descriptions':
          return [item.description, item.otherDescription]
            .filter(Boolean)
            .join(', ');
        case 'ids':
          return [item.id].filter(Boolean).join(', ');
        default:
          return '';
      }
    })
    .join(', ');

  return concatenatedString || null;
};

const handleSelectPolicieInfo = (clientInfo: any) => {
  selectedPolicieInfo.value = clientInfo;
  isModalOpen.value = true;
};

const handleClose = () => {
  isModalOpen.value = false;
  selectedPolicieInfo.value = null;

};

const handleShowInfoContainer = (data: any) => {
  detailContainer.value = data
  isModalContainerOpen.value = true
};

</script>
<template>
  <Layout :title="t('polizas.polizas.listar_polizas_maritimas_api')" :textColor="'color-secondary'">
    <template #layout>
      <div class="poliza">
        <ApiPolicyFilters
          type-policy-transport="Maritimo"
          :companies="companies"
          :show-company="!user?.apiKeyId"
          :show-download="!isAudit"
          :is-downloading-excel="isDownloadingExcel"
          @search="handleOnSubmit"
          @reset="handleClearFilters"
          @download="downloadExcel"
        />
        <DataTable
          :loading="isLoadingPolicies"
          :fields="fieldsTable"
          :items="policiesData"
          @update:model-value-page="onChangePage"
          :totalRows="paginationData?.count"
          :perPage="20"
          :currentPage="paginationData?.current_page"
          :hover="true"
        >
          <template v-if="true" v-slot:cell(actions)="policiesData">
            <div>
              <button
                v-if="!isAudit"
                class="background-pink p-1 border-0 w-100 text-white rounded-4"
                @click="handleDelete(policiesData.data.item)"
              >
			  	{{ t('global.cancelar') }}
              </button>
            </div>
          </template>
          <template v-if="true" v-slot:cell(details)="policiesData">
            <div>
              <button
                class="background-blue p-1 border-0 w-100 text-white rounded-4"
                @click="handleSelectPolicieInfo(policiesData.data.item)"
              >
			 	 {{ t('global.detalles') }}
              </button>
            </div>
          </template>
          <template v-if="true" v-slot:cell(customCatalog)="policiesData">
            <div class="d-flex align-self-center">
              <p
                class="truncatedText"
                :title="getRowTable(policiesData, 'descriptions')"
              >
                {{ getRowTable(policiesData, 'descriptions') }}
              </p>
            </div>
          </template>
          <template v-if="true" v-slot:cell(customCatalogIds)="policiesData">
            <div class="d-flex align-self-center">
              <p
                class="truncatedText"
                :title="getRowTable(policiesData, 'ids')"
              >
                {{ getRowTable(policiesData, 'ids') }}
              </p>
            </div>
          </template>
          <template v-if="true" v-slot:cell(detailsContainer)="policiesData">
            <div>
              <button
                v-if="policiesData.data.item.container"
                class="background-blue p-1 border-0 w-100 text-white rounded-4"
                @click="handleShowInfoContainer(policiesData.data.item.container)"
              >
			 	        {{ t('global.detalles') }}
              </button>
            </div>
          </template>
        </DataTable>
        <Dialog
          :centered="true"
          :visible="isOpenDialog"
          @cancel="isOpenDialog = false"
          @confirm="handleConfirmDelete"
        />
      </div>
      <BModal
        :modelValue="isModalOpen"
        hide-footer
        :centered="true"
        @hide="handleClose"
        static
        :title="t('polizas.polizas_maritimas.modal_title')"
        size="lg"
        >
        <p><strong>{{ t('polizas.polizas_maritimas.insured_name') }}: </strong><span>{{ selectedPolicieInfo?.insuredName }}</span></p>
        <p><strong>{{ t('polizas.polizas_maritimas.rfc') }}: </strong><span>{{ selectedPolicieInfo?.rfc }}</span></p>
        <p><strong>{{ t('polizas.polizas_maritimas.port_departure') }}: </strong><span>{{ selectedPolicieInfo?.portDeparture }}</span></p>
        <p><strong>{{ t('polizas.polizas_maritimas.pais_departure') }}: </strong><span>{{ selectedPolicieInfo?.countryDeparture?.description }}</span></p>
        <p><strong>{{ t('polizas.polizas_maritimas.port_arrival') }}: </strong><span>{{ selectedPolicieInfo?.portArrival }}</span></p>
        <p><strong>{{ t('polizas.polizas_maritimas.pais_arrival') }}: </strong><span>{{ selectedPolicieInfo?.countryArrival?.description }}</span></p>
        <p><strong>{{ t('polizas.polizas_maritimas.policy_type') }}: </strong><span>{{ selectedPolicieInfo?.policyType }}</span></p>
        <p><strong>{{ t('polizas.polizas_maritimas.indemnification_limit') }}: </strong><span>${{ selectedPolicieInfo?.indemnificationLimit }}</span></p>
        <p><strong>{{ t('polizas.polizas_maritimas.full_price') }}: </strong><span>${{ selectedPolicieInfo?.fullPrice }}</span></p>
        <p><strong>{{ t('polizas.polizas_maritimas.invoice') }}: </strong><span>{{ selectedPolicieInfo?.invoice }}</span></p>
        <p><strong>{{ t('polizas.polizas_maritimas.shipping_company') }}: </strong><span>{{ selectedPolicieInfo?.shippingCompany }}</span></p>
        <p><strong>{{ t('polizas.polizas_maritimas.container_number') }}: </strong><span>{{ selectedPolicieInfo?.containerNumber }}</span></p>
        <p><strong>{{ t('polizas.polizas_maritimas.container_type') }}: </strong><span>{{ selectedPolicieInfo?.containerTypeId }}</span></p>
        <p><strong>{{ t('polizas.polizas_maritimas.contact_mail') }}: </strong><span>{{ selectedPolicieInfo?.contactMail }}</span></p>
        <p><strong>{{ t('polizas.polizas_maritimas.contact_phone') }}: </strong><span>{{ selectedPolicieInfo?.contactPhone }}</span></p>
        <p><strong>{{ t('polizas.polizas_maritimas.driver_cellphone') }}: </strong><span>{{ selectedPolicieInfo?.driverCellphone }}</span></p>
        <p><strong>{{ t('polizas.polizas_maritimas.driver_email') }}: </strong><span>{{ selectedPolicieInfo?.driverEmail }}</span></p>
        <p><strong>{{ t('polizas.polizas_maritimas.driver_name') }}: </strong><span>{{ selectedPolicieInfo?.driverName }}</span></p>
        <p><strong>{{ t('polizas.polizas_maritimas.status') }}: </strong><span>{{ selectedPolicieInfo?.status }}</span></p>
        <p><strong>{{ t('polizas.polizas_maritimas.traicing_contact_mail') }}: </strong><span>{{ selectedPolicieInfo?.traicingContactMail }}</span></p>
        <p><strong>{{ t('polizas.polizas_maritimas.traicing_contact_name') }}: </strong><span>{{ selectedPolicieInfo?.traicingContactName }}</span></p>
        <p><strong>{{ t('polizas.polizas_maritimas.traicing_contact_phone') }}: </strong><span>{{ selectedPolicieInfo?.traicingContactPhone }}</span></p>
        <p><strong>{{ t('polizas.polizas_maritimas.travel_start_date') }}: </strong><span>{{ formatDateAndHourSpanish(selectedPolicieInfo?.travelStartDate) }}</span></p>
        <p><strong>{{ t('polizas.polizas_maritimas.travel_end_date') }}: </strong><span>{{ formatDateAndHourSpanish(selectedPolicieInfo?.travelEndDate) }}</span></p>
      </BModal>
    </template>
  </Layout>
</template>

<style scoped>
.truncatedText {
  max-width: 15rem;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  cursor: pointer;
  margin: 0;
}
</style>
